import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import api from '../../utils/api';

// --- Sidebar ---
const NAV = [
  { section: 'Main' },
  { id: 'dashboard', icon: '📊', label: 'Dashboard' },
  { section: 'Users' },
  { id: 'users', icon: '👥', label: 'Users' },
  { id: 'chats', icon: '💬', label: 'Chat History' },
  { section: 'Billing' },
  { id: 'plans', icon: '💎', label: 'Plans & Pricing' },
  { id: 'payments', icon: '💳', label: 'Payments' },
  { section: 'Settings' },
  { id: 'settings-general', icon: '⚙️', label: 'General Settings' },
  { id: 'settings-ai', icon: '🤖', label: 'AI Settings' },
  { id: 'settings-payment', icon: '💰', label: 'Payment Settings' },
  { id: 'settings-seo', icon: '🔍', label: 'SEO Settings' },
  { id: 'admins', icon: '🔐', label: 'Admin Accounts' },
  { id: 'change-password', icon: '🔑', label: 'Change Password' },
];

function Sidebar({ active, onNav }) {
  const admin = JSON.parse(localStorage.getItem('adminUser') || '{}');
  return (
    <div className="admin-sidebar">
      <div className="sidebar-logo">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#ff6b00,#ff8c3a)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 18 }}>φ</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>PhysicsAI Admin</div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{admin.role || 'admin'}</div>
          </div>
        </div>
      </div>
      <div className="sidebar-nav">
        {NAV.map((item, i) => item.section ? (
          <div key={i} className="sidebar-section">{item.section}</div>
        ) : (
          <button key={item.id} className={`sidebar-link ${active === item.id ? 'active' : ''}`} onClick={() => onNav(item.id)}>
            <span>{item.icon}</span> {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Dashboard View ---
function DashboardView() {
  const [data, setData] = useState(null);
  useEffect(() => { api.admin('dashboard').then(d => { if (d.success) setData(d.data); }); }, []);
  if (!data) return <div style={{ padding: 40, color: '#6b7280' }}>Loading...</div>;

  return (
    <div>
      <div className="stats-grid">
        {[
          { label: 'Total Users', value: data.total_users, icon: '👥', color: '#dbeafe', sub: `+${data.new_users_today} today` },
          { label: 'Total Revenue', value: `$${Number(data.total_revenue).toFixed(2)}`, icon: '💰', color: '#dcfce7', sub: 'All time' },
          { label: 'Active Subscriptions', value: data.active_subs, icon: '💎', color: '#fef3c7', sub: 'Paid plans' },
          { label: 'Total Chats', value: data.total_chats, icon: '💬', color: '#f3e8ff', sub: `${data.chats_today} today` },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div style={{ float: 'right' }}>
              <div className="stat-icon" style={{ background: s.color }}>{s.icon}</div>
            </div>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div className="admin-table-card">
          <h3>Recent Users</h3>
          <table className="admin-table">
            <thead><tr><th>Name</th><th>Plan</th><th>Joined</th></tr></thead>
            <tbody>{data.recent_users?.map(u => (
              <tr key={u.id}>
                <td><div style={{ fontWeight: 600 }}>{u.name}</div><div style={{ fontSize: 12, color: '#6b7280' }}>{u.email}</div></td>
                <td><span className={`badge ${u.plan === 'free' ? 'badge-blue' : 'badge-success'}`}>{u.plan}</span></td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{new Date(u.created_at).toLocaleDateString()}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div className="admin-table-card">
          <h3>Recent Payments</h3>
          <table className="admin-table">
            <thead><tr><th>User</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>{data.recent_payments?.map(p => (
              <tr key={p.id}>
                <td><div style={{ fontWeight: 600, fontSize: 13 }}>{p.name || 'Guest'}</div><div style={{ fontSize: 11, color: '#6b7280' }}>{p.gateway}</div></td>
                <td style={{ fontWeight: 700 }}>${p.amount}</td>
                <td><span className={`badge ${p.status === 'completed' ? 'badge-success' : p.status === 'pending' ? 'badge-warning' : 'badge-error'}`}>{p.status}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// --- Users View ---
function UsersView() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.admin('users', 'GET', null, `&page=${page}&search=${search}`)
      .then(d => { if (d.success) { setUsers(d.users); setTotal(d.total); } setLoading(false); });
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const toggleUser = async (user) => {
    await api.admin('user', 'PUT', { id: user.id, is_active: user.is_active ? 0 : 1 });
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <input style={{ flex: 1, padding: '10px 16px', border: '2px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none' }}
          placeholder="Search by name or email..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <button onClick={load} className="btn-primary" style={{ padding: '10px 20px', fontSize: 14 }}>Search</button>
      </div>
      <div className="admin-table-card">
        <h3>All Users <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 400 }}>({total} total)</span></h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead><tr><th>User</th><th>Plan</th><th>Queries</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</td></tr>
                : users.map(u => (
                  <tr key={u.id}>
                    <td><div style={{ fontWeight: 600 }}>{u.name}</div><div style={{ fontSize: 12, color: '#6b7280' }}>{u.email}</div></td>
                    <td><span className={`badge ${u.plan === 'free' ? 'badge-blue' : 'badge-success'}`}>{u.plan}</span></td>
                    <td style={{ fontSize: 13 }}>{u.queries_used} / {u.queries_limit === -1 ? '∞' : u.queries_limit}</td>
                    <td><span className={`badge ${u.is_active ? 'badge-success' : 'badge-error'}`}>{u.is_active ? 'Active' : 'Banned'}</span></td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      <button onClick={() => toggleUser(u)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, background: u.is_active ? '#fee2e2' : '#dcfce7', color: u.is_active ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                        {u.is_active ? 'Ban' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pagination">
        {Array.from({ length: Math.ceil(total / 20) }, (_, i) => (
          <button key={i} className={`page-btn ${page === i + 1 ? 'active' : ''}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
        ))}
      </div>
    </div>
  );
}

// --- Chat History ---
function ChatsView() {
  const [chats, setChats] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.admin('chats', 'GET', null, `&page=${page}`).then(d => { if (d.success) { setChats(d.chats); setTotal(d.total); } });
  }, [page]);

  return (
    <div className="admin-table-card">
      <h3>Chat History <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 400 }}>({total} total)</span></h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table">
          <thead><tr><th>User</th><th>Question</th><th>Tokens</th><th>Date</th></tr></thead>
          <tbody>
            {chats.map(c => (
              <tr key={c.id}>
                <td><div style={{ fontWeight: 600, fontSize: 13 }}>{c.name || 'Guest'}</div><div style={{ fontSize: 11, color: '#6b7280' }}>{c.email || 'N/A'}</div></td>
                <td style={{ maxWidth: 360, fontSize: 13 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.question}</div></td>
                <td style={{ fontSize: 13 }}>{c.tokens_used}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{new Date(c.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination" style={{ padding: '16px 0' }}>
        {Array.from({ length: Math.min(Math.ceil(total / 20), 10) }, (_, i) => (
          <button key={i} className={`page-btn ${page === i + 1 ? 'active' : ''}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
        ))}
      </div>
    </div>
  );
}

// --- Plans View ---
function PlansView() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => { api.admin('plans').then(d => { if (d.success) setPlans(d.plans); }); }, []);

  const save = async () => {
    const data = await api.admin('plan', 'POST', { ...editing, features: editing.features?.split ? editing.features.split('\n').filter(Boolean) : editing.features });
    if (data.success) { setMsg({ type: 'success', text: 'Plan saved!' }); setEditing(null); api.admin('plans').then(d => { if (d.success) setPlans(d.plans); }); }
    else setMsg({ type: 'error', text: data.error });
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div>
      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
      <div className="plans-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 20, marginBottom: 24 }}>
        {plans.map(plan => (
          <div key={plan.id} style={{ background: 'white', borderRadius: 16, border: '1px solid #e5e7eb', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#003d6e' }}>{plan.name}</span>
              <span className={`badge ${plan.is_active ? 'badge-success' : 'badge-error'}`}>{plan.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#ff6b00', marginBottom: 8 }}>${plan.price_monthly}<span style={{ fontSize: 14, color: '#6b7280' }}>/mo</span></div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{plan.queries_limit === -1 ? 'Unlimited' : plan.queries_limit} queries/month</div>
            <button onClick={() => setEditing({ ...plan, features: (plan.features || []).join('\n') })} style={{ width: '100%', padding: '9px', background: '#f8f9ff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Edit Plan</button>
          </div>
        ))}
      </div>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 36, maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: '#003d6e' }}>Edit {editing.name} Plan</h3>
            {[
              { key: 'price_monthly', label: 'Monthly Price ($)', type: 'number' },
              { key: 'price_yearly', label: 'Yearly Price ($)', type: 'number' },
              { key: 'queries_limit', label: 'Queries Limit (-1 = unlimited)', type: 'number' },
              { key: 'stripe_price_id_monthly', label: 'Stripe Price ID (Monthly)', type: 'text' },
              { key: 'stripe_price_id_yearly', label: 'Stripe Price ID (Yearly)', type: 'text' },
              { key: 'razorpay_plan_id_monthly', label: 'Razorpay Plan ID (Monthly)', type: 'text' },
              { key: 'paypal_plan_id_monthly', label: 'PayPal Plan ID (Monthly)', type: 'text' },
            ].map(f => (
              <div key={f.key} className="form-group-admin">
                <label>{f.label}</label>
                <input type={f.type} value={editing[f.key] || ''} onChange={e => setEditing({ ...editing, [f.key]: e.target.value })} />
              </div>
            ))}
            <div className="form-group-admin">
              <label>Features (one per line)</label>
              <textarea rows={5} value={editing.features || ''} onChange={e => setEditing({ ...editing, features: e.target.value })} />
            </div>
            <div className="form-group-admin">
              <label>Status</label>
              <select value={editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.value })}>
                <option value={1}>Active</option><option value={0}>Inactive</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={save} className="btn-primary" style={{ flex: 1, padding: '12px', justifyContent: 'center', fontSize: 15 }}>Save Plan</button>
              <button onClick={() => setEditing(null)} style={{ flex: 1, padding: '12px', border: '2px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', background: 'white' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Payments View ---
function PaymentsView() {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.admin('payments', 'GET', null, `&page=${page}`).then(d => { if (d.success) { setPayments(d.payments); setTotal(d.total); } });
  }, [page]);

  return (
    <div className="admin-table-card">
      <h3>Payments <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 400 }}>({total} total)</span></h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table">
          <thead><tr><th>User</th><th>Plan</th><th>Amount</th><th>Gateway</th><th>Cycle</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id}>
                <td><div style={{ fontWeight: 600, fontSize: 13 }}>{p.name || 'Unknown'}</div><div style={{ fontSize: 11, color: '#6b7280' }}>{p.email}</div></td>
                <td style={{ fontSize: 13 }}>{p.plan_name}</td>
                <td style={{ fontWeight: 700, color: '#003d6e' }}>${p.amount}</td>
                <td><span className="badge badge-blue">{p.gateway}</span></td>
                <td style={{ fontSize: 12 }}>{p.billing_cycle}</td>
                <td><span className={`badge ${p.status === 'completed' ? 'badge-success' : p.status === 'pending' ? 'badge-warning' : 'badge-error'}`}>{p.status}</span></td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{new Date(p.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Settings View ---
function SettingsView({ group, title }) {
  const [settings, setSettings] = useState({});
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.admin('settings', 'GET', null, `&group=${group}`).then(d => { if (d.success) setSettings(d.settings); });
  }, [group]);

  const save = async () => {
    setLoading(true);
    const updates = {};
    Object.keys(settings).forEach(k => { updates[k] = settings[k].value; });
    const data = await api.admin('settings', 'POST', { settings: updates });
    setLoading(false);
    setMsg(data.success ? { type: 'success', text: 'Settings saved!' } : { type: 'error', text: data.error });
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div className="admin-form">
      <h3>{title}</h3>
      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
      {Object.values(settings).map(s => (
        <div key={s.key_name} className="form-group-admin">
          <label>{s.label}</label>
          {s.type === 'textarea' ? (
            <textarea rows={5} value={s.value || ''} onChange={e => setSettings({ ...settings, [s.key_name]: { ...s, value: e.target.value } })} />
          ) : s.type === 'boolean' ? (
            <select value={s.value} onChange={e => setSettings({ ...settings, [s.key_name]: { ...s, value: e.target.value } })}>
              <option value="1">Enabled</option><option value="0">Disabled</option>
            </select>
          ) : (
            <input type={s.type === 'password' ? 'password' : 'text'} value={s.value || ''} onChange={e => setSettings({ ...settings, [s.key_name]: { ...s, value: e.target.value } })} placeholder={s.type === 'password' ? 'Enter new value to update' : ''} />
          )}
        </div>
      ))}
      <button onClick={save} className="btn-primary" style={{ padding: '12px 32px', fontSize: 15, justifyContent: 'center' }} disabled={loading}>
        {loading ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}

// --- SEO View ---
function SeoView() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);
  const robotsContent = '';

  useEffect(() => { api.admin('seo').then(d => { if (d.success) setPages(d.pages); }); }, []);

  const save = async () => {
    const data = await api.admin('seo', 'POST', editing);
    if (data.success) { setMsg({ type: 'success', text: 'SEO page saved!' }); setEditing(null); api.admin('seo').then(d => { if (d.success) setPages(d.pages); }); }
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div>
      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
      <SettingsView group="seo" title="SEO & Analytics Settings" />
      <div className="admin-table-card" style={{ marginTop: 24 }}>
        <h3>
          SEO Pages
          <button className="btn-primary" style={{ fontSize: 13, padding: '8px 16px' }} onClick={() => setEditing({ page_slug: '', title: '', meta_description: '', meta_keywords: '', og_title: '', og_description: '', canonical_url: '', schema_markup: '', is_indexed: 1 })}>+ Add Page</button>
        </h3>
        <table className="admin-table">
          <thead><tr><th>Page</th><th>Title</th><th>Description</th><th>Indexed</th><th>Actions</th></tr></thead>
          <tbody>
            {pages.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 600, fontSize: 13 }}>{p.page_slug}</td>
                <td style={{ fontSize: 13, maxWidth: 200 }}>{p.title}</td>
                <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 240 }}>{p.meta_description?.substring(0, 80)}...</td>
                <td><span className={`badge ${p.is_indexed ? 'badge-success' : 'badge-warning'}`}>{p.is_indexed ? 'Yes' : 'No'}</span></td>
                <td><button onClick={() => setEditing(p)} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: 12 }}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 36, maxWidth: 620, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: '#003d6e' }}>Edit SEO Page</h3>
            {[
              { key: 'page_slug', label: 'Page Slug (e.g., /, /pricing)' },
              { key: 'title', label: 'Meta Title' },
              { key: 'meta_description', label: 'Meta Description', textarea: true },
              { key: 'meta_keywords', label: 'Meta Keywords' },
              { key: 'og_title', label: 'OG Title' },
              { key: 'og_description', label: 'OG Description', textarea: true },
              { key: 'og_image', label: 'OG Image URL' },
              { key: 'canonical_url', label: 'Canonical URL' },
              { key: 'schema_markup', label: 'Schema Markup JSON', textarea: true },
            ].map(f => (
              <div key={f.key} className="form-group-admin">
                <label>{f.label}</label>
                {f.textarea ? (
                  <textarea rows={3} value={editing[f.key] || ''} onChange={e => setEditing({ ...editing, [f.key]: e.target.value })} />
                ) : (
                  <input type="text" value={editing[f.key] || ''} onChange={e => setEditing({ ...editing, [f.key]: e.target.value })} />
                )}
              </div>
            ))}
            <div className="form-group-admin">
              <label>Indexed by Search Engines</label>
              <select value={editing.is_indexed} onChange={e => setEditing({ ...editing, is_indexed: e.target.value })}>
                <option value={1}>Yes - Index this page</option><option value={0}>No - Noindex</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={save} className="btn-primary" style={{ flex: 1, padding: '12px', justifyContent: 'center', fontSize: 15 }}>Save Page</button>
              <button onClick={() => setEditing(null)} style={{ flex: 1, padding: '12px', border: '2px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', background: 'white' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Admin Accounts ---
function AdminAccountsView() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [msg, setMsg] = useState(null);

  useEffect(() => { api.admin('admins').then(d => { if (d.success) setAdmins(d.admins); }); }, []);

  const addAdmin = async () => {
    const data = await api.admin('admin', 'POST', form);
    if (data.success) { setMsg({ type: 'success', text: 'Admin added!' }); setForm({ name: '', email: '', password: '', role: 'admin' }); api.admin('admins').then(d => { if (d.success) setAdmins(d.admins); }); }
    else setMsg({ type: 'error', text: data.error });
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div>
      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
      <div className="admin-form" style={{ maxWidth: 480 }}>
        <h3>Add New Admin</h3>
        {[{ key: 'name', label: 'Full Name' }, { key: 'email', label: 'Email' }, { key: 'password', label: 'Password', type: 'password' }].map(f => (
          <div key={f.key} className="form-group-admin">
            <label>{f.label}</label>
            <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
          </div>
        ))}
        <div className="form-group-admin">
          <label>Role</label>
          <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            <option value="admin">Admin</option><option value="moderator">Moderator</option><option value="superadmin">Super Admin</option>
          </select>
        </div>
        <button onClick={addAdmin} className="btn-primary" style={{ padding: '12px 24px', fontSize: 14, justifyContent: 'center' }}>Add Admin</button>
      </div>

      <div className="admin-table-card">
        <h3>Admin Accounts</h3>
        <table className="admin-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Last Login</th><th>Status</th></tr></thead>
          <tbody>
            {admins.map(a => (
              <tr key={a.id}>
                <td style={{ fontWeight: 600 }}>{a.name}</td>
                <td style={{ fontSize: 13, color: '#6b7280' }}>{a.email}</td>
                <td><span className={`badge ${a.role === 'superadmin' ? 'badge-error' : 'badge-blue'}`}>{a.role}</span></td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{a.last_login ? new Date(a.last_login).toLocaleString() : 'Never'}</td>
                <td><span className={`badge ${a.is_active ? 'badge-success' : 'badge-error'}`}>{a.is_active ? 'Active' : 'Inactive'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Change Password ---
function ChangePasswordView() {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (form.new_password !== form.confirm) { setMsg({ type: 'error', text: 'Passwords do not match' }); return; }
    if (form.new_password.length < 8) { setMsg({ type: 'error', text: 'Password must be at least 8 characters' }); return; }
    setLoading(true);
    const data = await api.admin('change-password', 'POST', { current_password: form.current_password, new_password: form.new_password });
    setLoading(false);
    setMsg(data.success ? { type: 'success', text: data.message } : { type: 'error', text: data.error });
    if (data.success) setForm({ current_password: '', new_password: '', confirm: '' });
    setTimeout(() => setMsg(null), 4000);
  };

  return (
    <div className="admin-form" style={{ maxWidth: 420 }}>
      <h3>Change Admin Password</h3>
      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
      {[
        { key: 'current_password', label: 'Current Password' },
        { key: 'new_password', label: 'New Password (min. 8 characters)' },
        { key: 'confirm', label: 'Confirm New Password' },
      ].map(f => (
        <div key={f.key} className="form-group-admin">
          <label>{f.label}</label>
          <input type="password" value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
        </div>
      ))}
      <button onClick={save} className="btn-primary" style={{ padding: '12px 28px', fontSize: 15, justifyContent: 'center' }} disabled={loading}>
        {loading ? 'Updating...' : 'Update Password'}
      </button>
    </div>
  );
}

// --- Main Admin Dashboard ---
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState('dashboard');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) navigate('/admin/login');
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const PAGE_TITLES = {
    dashboard: 'Dashboard',
    users: 'User Management',
    chats: 'Chat History',
    plans: 'Plans & Pricing',
    payments: 'Payment History',
    'settings-general': 'General Settings',
    'settings-ai': 'AI Configuration',
    'settings-payment': 'Payment Gateway Settings',
    'settings-seo': 'SEO & Analytics',
    admins: 'Admin Accounts',
    'change-password': 'Change Password',
  };

  const renderContent = () => {
    switch (active) {
      case 'dashboard': return <DashboardView />;
      case 'users': return <UsersView />;
      case 'chats': return <ChatsView />;
      case 'plans': return <PlansView />;
      case 'payments': return <PaymentsView />;
      case 'settings-general': return <SettingsView group="general" title="General Site Settings" />;
      case 'settings-ai': return <SettingsView group="ai" title="AI Configuration (OpenAI)" />;
      case 'settings-payment': return <SettingsView group="payment" title="Payment Gateway Settings" />;
      case 'settings-seo': return <SeoView />;
      case 'admins': return <AdminAccountsView />;
      case 'change-password': return <ChangePasswordView />;
      default: return <DashboardView />;
    }
  };

  return (
    <div className="admin-layout">
      <Sidebar active={active} onNav={setActive} />
      <div className="admin-main">
        <div className="admin-topbar">
          <h1>{PAGE_TITLES[active] || 'Admin'}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <a href="/" target="_blank" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>🌐 View Site</a>
            <button onClick={logout} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: 13, background: 'white', color: '#6b7280' }}>Sign Out</button>
          </div>
        </div>
        <div className="admin-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
